<script setup lang="ts">
  import { useSmeCampaignStore } from '@/store/sme/campaigns';
  import msApi from '@/utils/msApi';
  import { getSme } from '@/utils/request';
  import { capitalizeFirstLetter, formatDateTime, formatNumber } from '@/utils/strings';
  import { computed, onBeforeMount, onMounted, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import SmeDetailsStat from './components/SmeDetailsStat.vue';
  import ReportEngagementChart from './components/ReportEngagementChart.vue';
  import CreatorPerformanceChart from './components/CreatorPerformanceChart.vue';
  import ReportAgeChart from './components/ReportAgeChart.vue';
  import ReportGenderChart from './components/ReportGenderChart.vue';
  import CreatorSocialMetrics from './components/CreatorSocialMetrics.vue';
  import AiInsights from './components/modals/AiInsights.vue';
  import SmeCampaignReportLoader from './components/loaders/SmeCampaignReportLoader.vue';
  import { getPackageByBudget } from '../../utils/getPackage';
  import { useSmeCampaignReportStore } from '@/store/sme/sme-campaign-report';
  import { defaultAudienceData, defaultPerformances, defaultStats } from './data/default-report-data';
  import usePlatformAnalytics from '@/composables/usePlatformAnalytics';
  import ShareReportModal from './components/ShareReportModal.vue';
  import ReportReachChart from './components/ReportReachChart.vue';

  const { trackEvent } = usePlatformAnalytics(true);

  const route = useRoute();
  const router = useRouter();

  const campaignId = route.params.campaign_id as string;

  const smeCampaignStore = useSmeCampaignStore();
  const smeCampaignReportStore = useSmeCampaignReportStore();

  const campaignMetrics = ref<any>({ data: null });
  const isFetchingMetrics = ref(true);

  const campaignDetails = ref<any>();
  const isFetchingDetails = ref(true);

  const audienceDemographics = ref<any>();
  const isFetchingDemographics = ref(true);

  const showInsightsModal = ref(false);
  const showShareReportModal = ref(false);

  // Track if we're using cached data
  const hasInitialData = ref(false);

  const smeCampaign = computed(() => {
    const campaign = smeCampaignStore.campaigns.find(campaign => campaign.id === campaignId);

    return campaign;
  });

  const campaignDescription = computed(() => {
    if (!campaignDetails.value) return;
    return campaignDetails.value.description;
  });

  const campaignStartDate = computed(() => smeCampaign.value?.start_date.split('T')[0]);
  const campaignEndDate = computed(() => smeCampaign.value?.end_date.split('T')[0]);

  const formattedStartDate = computed(() => formatDateTime(campaignStartDate.value!, { format: 'MMM DD, YYYY' }));
  const formattedEndDate = computed(() => formatDateTime(campaignEndDate.value!, { format: 'MMM DD, YYYY' }));

  const selectedBudgetPackage = computed(() => getPackageByBudget(campaignDetails.value.budget));

  // Check for cached data on mount
  const cachedData = computed(() => {
    const cachedData = smeCampaignReportStore.getCampaignReportData(campaignId);

    if (cachedData) {
      return cachedData;
    }

    return null;
  });

  const getCampaignMetrics = async () => {
    if (cachedData.value) {
      const metricValue = { data: cachedData.value.campaignMetrics.data };
      console.log('Metric value: ', metricValue);

      campaignMetrics.value = { data: cachedData.value.campaignMetrics.data };
      isFetchingMetrics.value = false;

      return true;
    }

    try {
      isFetchingMetrics.value = true;
      const res = await msApi.smePost(`/reporting/campaign_metrics`, {
        campaign_id: campaignId,
        end_date: '',
        influencer_ids: [],
        platforms: [],
        start_date: '',
      });

      if (res) {
        if (Array.isArray(res.data) && res.data.length === 0) {
          const emptyData = {
            influencers: [],
            performances: defaultPerformances,
            platforms: [],
            stats: defaultStats,
          };

          console.log('Empty response...using empty data');
          campaignMetrics.value = { data: emptyData };
        } else {
          campaignMetrics.value = { data: res.data };
        }
        console.log('Campaign Metrics: ', campaignMetrics.value);
        return true;
      }
      return false;
    } catch (error) {
      const emptyData = {
        influencers: [],
        performances: defaultPerformances,
        platforms: [],
        stats: defaultStats,
      };

      console.log('Request failed...using empty data');
      campaignMetrics.value = { data: emptyData };
      console.log('Campaign Metrics: ', campaignMetrics.value);
      return false;
    } finally {
      isFetchingMetrics.value = false;
    }
  };

  const getCampaignDemographics = async () => {
    try {
      isFetchingDemographics.value = true;
      const res = await msApi.smePost('/reporting/audience_data', {
        campaign_id: campaignId,
      });

      if (res) {
        if (!res.data) {
          const emptyData = defaultAudienceData;

          audienceDemographics.value = { data: emptyData };
        } else {
          audienceDemographics.value = { data: res.data };
        }
      }
    } catch (error) {
      const emptyData = defaultAudienceData;

      audienceDemographics.value = { data: emptyData };
    } finally {
      isFetchingDemographics.value = false;
    }
  };

  const getCampaignDetails = async () => {
    if (cachedData.value) {
      campaignDetails.value = cachedData.value.campaignDetails;
      isFetchingDetails.value = false;
      return;
    }

    try {
      isFetchingDetails.value = true;
      campaignDetails.value = await getSme(`/campaigns/${campaignId}`, {
        campaign_id: campaignId,
      });
    } catch (error) {
      console.log(error);
    } finally {
      isFetchingDetails.value = false;
    }
  };

  const stats = computed(() => {
    const info = [
      {
        title: 'reach',
        value: campaignMetrics.value?.data?.stats?.reach?.value ?? 0,
      },
      {
        title: 'views',
        value: campaignMetrics.value?.data?.stats?.impressions?.value ?? 0,
      },
      {
        title: 'engagement',
        value: campaignMetrics.value?.data?.stats?.engagement_rate?.value ?? 0,
      },
    ];

    return info;
  });

  const chartLabels = computed(() => {
    if (!campaignMetrics.value) return;
    return campaignMetrics.value?.data?.performances?.label ?? 0;
  });

  const chartData = computed(() => {
    if (!campaignMetrics.value) return;

    let data = {
      reach: [],
      engagement_rate: [],
    };

    data.reach = campaignMetrics.value?.data?.performances?.data?.reach ?? 0;
    data.engagement_rate = campaignMetrics.value?.data?.performances?.data?.engagement_rate ?? 0;

    return data;
  });

  const creatorAggregatedData = computed(() => {
    if (!campaignMetrics.value?.data?.influencers) return [];

    const aggregated = campaignMetrics.value.data.influencers.reduce((acc: any, influencer: any) => {
      const id = influencer.influencer_id;
      if (!acc[id]) {
        acc[id] = {
          ...influencer,
          platform: '',
          deliverables: [{ ...influencer }],
        };
      } else {
        // Aggregate numeric values
        acc[id].impressions += influencer.impressions || 0;
        acc[id].reach += influencer.reach || 0;
        acc[id].total_interactions += influencer.total_interactions || 0;
        acc[id].saved += influencer.saved || 0;
        acc[id].shares += influencer.shares || 0;
        acc[id].cost += influencer.cost || 0;
        acc[id].occurrences += influencer.occurrences || 0;

        // Add to deliverables
        acc[id].deliverables.push({ ...influencer });
      }
      return acc;
    }, {});

    // Average rates for the aggregated objects
    return Object.values(aggregated).map((creator: any) => {
      if (creator.deliverables.length > 1) {
        const count = creator.deliverables.length;
        creator.engagement_rate = creator.deliverables.reduce((sum: number, d: any) => sum + (d.engagement_rate || 0), 0) / count;
        creator.cpm = creator.deliverables.reduce((sum: number, d: any) => sum + (d.cpm || 0), 0) / count;
        creator.cpe = creator.deliverables.reduce((sum: number, d: any) => sum + (d.cpe || 0), 0) / count;
        creator.cpr = creator.deliverables.reduce((sum: number, d: any) => sum + (d.cpr || 0), 0) / count;
      }
      return creator;
    });
  });

  const creatorsData = computed(() => {
    return creatorAggregatedData.value || [];
    // if (!campaignMetrics.value) return;
    // return campaignMetrics.value?.data?.influencers ?? [];
  });

  const toggleInsightsModal = () => (showInsightsModal.value = !showInsightsModal.value);
  const toggleShareReportModal = () => (showShareReportModal.value = !showShareReportModal.value);

  onBeforeMount(async () => {
    await getCampaignDetails();
  });

  onMounted(async () => {
    if (cachedData.value) {
      hasInitialData.value = true;
    }

    trackEvent({
      event_name: 'CampaignReportDetailsView',
      platform: 'sme',
      metadata: {
        campaign_id: campaignId,
      },
    });

    const metricsSuccess = await getCampaignMetrics();

    if (metricsSuccess) {
      await getCampaignDemographics();
    } else {
      // Set demographics to default data if metrics failed
      console.log('Skipping demographics request due to metrics failure');
      audienceDemographics.value = { data: defaultAudienceData };
      isFetchingDemographics.value = false;
    }
  });
</script>

<template>
  <main class="sme-report-wrapper screen-wrapper flex flex-col gap-24">
    <button class="back-to-campaign-btn flex items-center self-start gap-16" @click="router.push({ name: 'sme-reports' })">
      <iconify-icon icon="lucide:arrow-left" />
      <span>Back to reports</span>
    </button>
    <template v-if="isFetchingDetails">
      <SmeCampaignReportLoader />
    </template>
    <template v-else>
      <div class="hero-section flex items-center justify-between" v-if="smeCampaign">
        <div class="welcome-container">
          <h1 class="welcome-message">{{ smeCampaign.name }}</h1>
          <template v-if="isFetchingDetails">
            <div class="skeleton-metric-label"></div>
          </template>
          <div class="pill-container flex items-center gap-4">
            <span class="pill channel-pill" v-for="channel in smeCampaign.channels" :key="channel">
              {{ capitalizeFirstLetter(channel.toLowerCase()) }}
            </span>
            <span class="pill" :class="smeCampaign.campaign_status_for_merchant.toLowerCase()">
              {{ capitalizeFirstLetter(smeCampaign.campaign_status_for_merchant.toLowerCase()) }}
            </span>
            <span class="flex items-center gap-4 campaign-date">
              <iconify-icon icon="lucide:calendar" />
              {{ formattedStartDate }} - {{ formattedEndDate }}
            </span>
          </div>
        </div>
      </div>
      <div class="details-stat-wrapper flex flex-col gap-16" v-if="smeCampaign">
        <div class="call-to-action-container self-end flex items-center gap-12">
          <!-- <button class="insights-btn flex items-center justify-center gap-8" @click="toggleInsightsModal">
            <iconify-icon icon="lucide:brain" />
            <span>AI Insights</span>
          </button> -->
          <button class="export-btn flex items-center justify-center gap-8" @click="toggleShareReportModal">
            <iconify-icon icon="lucide:arrow-up-to-line" />
            <span>Share</span>
          </button>
        </div>
        <div class="details-stat-container flex items-center">
          <SmeDetailsStat v-for="stat in stats" :stat="stat" :key="stat.value" />
          <div class="budget-stat flex flex-col gap-8">
            <span class="stat-title">{{ selectedBudgetPackage?.name }} Package</span>
            <span class="value">{{ `${smeCampaign.currency} ${formatNumber(smeCampaign.budget)}` }}</span>
          </div>
        </div>
        <div class="chart-container">
          <ReportReachChart :labels="chartLabels" :data="chartData" />

          <ReportEngagementChart :labels="chartLabels" :data="chartData" />

          <CreatorPerformanceChart :creators="creatorsData" />

          <template v-if="audienceDemographics && audienceDemographics.data">
            <ReportAgeChart :data="audienceDemographics.data.gender" />
            <ReportGenderChart :gender="audienceDemographics.data.age" />
          </template>
        </div>
        <div class="creator-performance-details-container flex flex-col gap-16" v-if="creatorsData.length > 0">
          <h2 class="section-heading">Creator Performance Details</h2>
          <div class="metrics-container flex flex-col gap-16">
            <CreatorSocialMetrics v-for="influencer in creatorsData" :key="influencer.influencer_id" :influencer="influencer" />
          </div>
        </div>
      </div>
    </template>
    <AiInsights v-if="showInsightsModal" :campaign_id="campaignId" @close-modal="toggleInsightsModal" />
    <ShareReportModal
      v-if="showShareReportModal && smeCampaign"
      :id="campaignId"
      :campaignName="smeCampaign.name"
      :channel="smeCampaign.channels[0]"
      :status="smeCampaign.campaign_status_for_merchant"
      :startDate="smeCampaign.start_date"
      :endDate="smeCampaign.end_date"
      @close-modal="toggleShareReportModal"
    />
  </main>
</template>

<style scoped lang="scss">
  @import url('./styles/sme-campaign-report.scss');
</style>
